import { create } from "zustand"
import { API, NexusSuccessResponse } from "../api/API"
import {
    DataReceiptDto,
    DataReceiptForm,
    ReceiptShippingInfoWrapper,
    SingleDataReceiptDto,
} from "../models/DataReceipt"
import { isValidDataReceipt, transformShippingInfoToReceiptDetailDto } from "../utils/DataReceiptUtils"
import { TimeUtilsDateFns } from "../utils/TimeUtils"
import { useAppErrorStore } from "./AppErrorStore"
import {
    DataReceiptTableStoreType,
    createDataReceiptTableSlice,
    initDataReceiptTableState,
} from "./DataReceiptTableSlice"

interface DataReceiptState {
    validDataReceipt: boolean
    receiptDetailsForm: DataReceiptForm
    selectedShippingInfoWrappers: ReceiptShippingInfoWrapper[]
    dialogShippingInfoWrapper?: ReceiptShippingInfoWrapper
    isFetchingDataReceiptFormData: boolean
    deletedDetails: number[]
}

interface DataReceiptActions {
    setReceiptDetailsForm: (data: DataReceiptForm) => void
    setSelectedShippingInfoWrappers: (wrappers: ReceiptShippingInfoWrapper[]) => void
    setDialogShippingInfoWrapper: (wrapper?: ReceiptShippingInfoWrapper) => void
    setDeletedDetails: (deletedDetailIds: number[]) => void
    getDataReceiptForCreation: () => DataReceiptDto
    fetchDataReceiptFormData: (id: string) => void
    clearState: () => void
}

const initState: () => DataReceiptState = () => ({
    validDataReceipt: false,
    selectedShippingInfoWrappers: [],
    deletedDetails: [],
    isFetchingDataReceiptFormData: false,
    receiptDetailsForm: {
        date_received: TimeUtilsDateFns.formatDate(new Date()),
        office: undefined,
        received_by: undefined,
    },
})

type DataReceiptStoreType = DataReceiptState & DataReceiptActions & DataReceiptTableStoreType

export const useDataReceiptStore = create<DataReceiptStoreType>((set, get, ...rest) => ({
    ...createDataReceiptTableSlice(set, get, ...rest),
    ...initState(),
    setReceiptDetailsForm: (data) => {
        set((state) => ({
            receiptDetailsForm: data,
            validDataReceipt: isValidDataReceipt(data, state.selectedShippingInfoWrappers),
        }))
    },
    setSelectedShippingInfoWrappers: (wrappers) => {
        const { receiptDetailsForm } = get()
        set({
            selectedShippingInfoWrappers: wrappers,
            validDataReceipt: isValidDataReceipt(receiptDetailsForm, wrappers),
        })
    },
    setDialogShippingInfoWrapper: (wrapper) => set({ dialogShippingInfoWrapper: wrapper }),
    setDeletedDetails: (deletedDetailIds) => set({ deletedDetails: deletedDetailIds }),
    getDataReceiptForCreation: () => {
        const { receiptDetailsForm, selectedShippingInfoWrappers, deletedDetails } = get()

        if (
            receiptDetailsForm.date_received === "" ||
            receiptDetailsForm.office == null ||
            receiptDetailsForm.received_by == null
        ) {
            throw new Error("Unable to transform form details into CreateDataReceipt")
        }

        const createDataReceipt: DataReceiptDto = {
            id: receiptDetailsForm.id,
            received_by: receiptDetailsForm.received_by,
            date_received: receiptDetailsForm.date_received,
            office: receiptDetailsForm.office,
            details: selectedShippingInfoWrappers.map(transformShippingInfoToReceiptDetailDto),
            deleted_details: deletedDetails,
        }
        return createDataReceipt
    },
    fetchDataReceiptFormData: async (dataReceiptId) => {
        set({ isFetchingDataReceiptFormData: true })
        try {
            const dataReceiptResult: NexusSuccessResponse<SingleDataReceiptDto> = await API.get(
                `/data-receipts-with-detailed-details/${dataReceiptId}`,
                `Error retrieving data receipt with id ${dataReceiptId}`,
            )
            const dataReceipt = dataReceiptResult.data

            const form: DataReceiptForm = {
                id: dataReceipt.id,
                date_received: dataReceipt.date_received,
                received_by: dataReceipt.received_by,
                office: dataReceipt.office,
            }

            const wrappers: ReceiptShippingInfoWrapper[] = []
            const deletedDetails: number[] = []
            dataReceipt.details.forEach((detail, index) => {
                // FIXME - there was a bug that allowed for data receipts details
                //  to not link to missions/shipping_info
                //  this is a temporary measure to assure that connection exists when editing
                if (detail.shipping_info && detail.mission) {
                    const wrapper: ReceiptShippingInfoWrapper = {
                        fe_id: index + 1,
                        shipping_info: detail.shipping_info,
                        mission: detail.mission,
                        detail: {
                            id: detail.id,
                            size_in_gb: detail.size_in_gb,
                            status: detail.status,
                            notes: detail.notes,
                        },
                    }
                    wrappers.push(wrapper)
                } else {
                    // FIXME this only is triggered as long as there is missing shipping_info/mission
                    detail.id && deletedDetails.push(detail.id)
                }
            })

            set({
                receiptDetailsForm: form,
                selectedShippingInfoWrappers: wrappers,
                deletedDetails,
                isFetchingDataReceiptFormData: false,
            })
        } catch (error) {
            useAppErrorStore.getState().setAppError(error.message)
            set({ isFetchingDataReceiptFormData: false })
        }
    },
    clearState: () => set({ ...initState(), ...initDataReceiptTableState() }),
}))
